import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { CheckCircle, Loader2, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Brand-side onboarding — landing page for magic-link invites emailed by
 * Freddy Media ops via /api/admin/brands/{id}/invite.
 */
export default function BrandOnboarding() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const [state, setState] = useState('loading'); // loading | ready | error | submitting | done
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({ contact_name: '', password: '', confirmPassword: '', industry: '', website: '', phone: '' });

  useEffect(() => {
    if (!token) { setState('error'); setError('No invite token in URL.'); return; }
    axios.get(`${API}/portal/onboarding/preview?token=${token}`)
      .then(r => {
        setPreview(r.data);
        setForm(f => ({
          ...f,
          contact_name: r.data.contact_name || '',
          industry: r.data.industry || '',
          website: r.data.website || '',
        }));
        setState('ready');
      })
      .catch(e => {
        setState('error');
        setError(e.response?.data?.detail || 'Invite link is invalid or expired.');
      });
  }, [token]);

  const submit = async () => {
    if (form.password.length < 8) { toast.error('Password must be at least 8 characters.'); return; }
    if (form.password !== form.confirmPassword) { toast.error("Passwords don't match."); return; }
    if (!form.contact_name.trim()) { toast.error('Contact name is required.'); return; }
    setState('submitting');
    try {
      const r = await axios.post(`${API}/portal/onboarding/redeem`, {
        token,
        contact_name: form.contact_name,
        password: form.password,
        industry: form.industry,
        website: form.website,
        phone: form.phone,
      });
      localStorage.setItem('propost_brand_token', r.data.access_token);
      setState('done');
      setTimeout(() => navigate('/portal?tab=settings'), 1600);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Onboarding failed');
      setState('ready');
    }
  };

  return (
    <div className="min-h-screen bg-[#090E33] flex items-center justify-center p-4" data-testid="brand-onboarding">
      <Toaster position="top-right" theme="dark" richColors />
      <div className="max-w-md w-full">
        <div className="text-center mb-6">
          <img src="https://customer-assets.emergentagent.com/job_campaign-board/artifacts/pflz8b2v_freddy-logo-symbol.png" alt="" className="h-9 mx-auto mb-3" />
          <h1 className="font-display text-2xl font-bold text-white">Finish setting up <span className="text-[#FF4343]">Freddy Leads</span></h1>
        </div>

        {state === 'loading' && (
          <div className="bg-[#111827] border border-white/10 p-6 text-center text-[#9CA3AF]">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Checking your invite…
          </div>
        )}

        {state === 'error' && (
          <div className="bg-[#111827] border border-[#FF4343]/30 p-6" data-testid="onboarding-error">
            <h3 className="text-lg font-bold text-[#FF4343] mb-2">This link isn't valid</h3>
            <p className="text-sm text-[#9CA3AF] mb-4">{error}</p>
            <p className="text-xs text-[#6B7280]">Reach out to the Freddy Media team and we'll send a fresh link.</p>
          </div>
        )}

        {state === 'done' && (
          <div className="bg-[#111827] border border-[#10B981]/30 p-6 text-center" data-testid="onboarding-done">
            <CheckCircle className="w-10 h-10 text-[#10B981] mx-auto mb-3" />
            <h3 className="text-lg font-bold text-white mb-1">You're in.</h3>
            <p className="text-sm text-[#9CA3AF]">Redirecting you to the portal…</p>
          </div>
        )}

        {(state === 'ready' || state === 'submitting') && preview && (
          <div className="bg-[#111827] border border-white/10 p-6">
            <p className="text-[10px] font-mono uppercase text-[#FF4343] tracking-wider mb-1">Welcome, {preview.brand_name}</p>
            <p className="text-xs text-[#9CA3AF] mb-5">
              Your Freddy Media ops team has set up your account. Just confirm a couple of details and pick a password — we'll email you when your first campaign is ready to review.
            </p>
            <div className="space-y-3">
              {[
                { key: 'contact_name', label: 'Your name', required: true },
                { key: 'industry', label: 'Industry' },
                { key: 'website', label: 'Website' },
                { key: 'phone', label: 'Phone (optional)' },
              ].map(f => (
                <div key={f.key}>
                  <label className="text-[10px] font-mono uppercase text-[#6B7280] block mb-1">{f.label}{f.required && ' *'}</label>
                  <input value={form[f.key]}
                    onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                    data-testid={`onboarding-${f.key}`}
                    className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
                </div>
              ))}
              <div>
                <label className="text-[10px] font-mono uppercase text-[#6B7280] block mb-1">Portal password *</label>
                <input type="password" value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  data-testid="onboarding-password"
                  placeholder="Min 8 characters"
                  className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase text-[#6B7280] block mb-1">Confirm password *</label>
                <input type="password" value={form.confirmPassword}
                  onChange={e => setForm({ ...form, confirmPassword: e.target.value })}
                  data-testid="onboarding-confirm-password"
                  className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
              </div>
            </div>
            <button onClick={submit} disabled={state === 'submitting'}
              data-testid="onboarding-submit"
              className="w-full mt-5 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] py-3 text-sm font-semibold hover:bg-[#E03030] disabled:opacity-50 flex items-center justify-center gap-2">
              {state === 'submitting' ? 'Activating…' : <>Activate account <ArrowRight className="w-4 h-4" /></>}
            </button>
            <p className="text-[10px] text-[#6B7280] text-center mt-3">You'll be able to add a credit card from the Settings tab once you're in.</p>
          </div>
        )}
      </div>
    </div>
  );
}
